import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Trash2, Save, ArrowLeft, Ruler } from 'lucide-react';
import axios from "axios";

// --- API Endpoints ---
const API_BASE_URL = 'https://tailor.beezetsoftwaresolution.com/api/order';
const GET_COSTUME_URL = 'https://tailor.beezetsoftwaresolution.com/api/costume/get_costume.php';
const GET_EMPLOYEE_URL = 'https://tailor.beezetsoftwaresolution.com/api/employee/get_employees.php';
const GET_ORDERS_URL = `${API_BASE_URL}/get_orders.php`;
const EDIT_ORDERS_URL = `${API_BASE_URL}/edit_orders.php`;
const ADD_ITEM_URL = `${API_BASE_URL}/add_items_to_order.php`;
const UPDATE_ITEM_EMPLOYEE_URL = `${API_BASE_URL}/update_order_item_employee.php`;
const DELETE_ITEM_URL = `${API_BASE_URL}/delete_order_item.php`;

// --- Mock Lookups (REPLACE WITH REAL API CALLS IF NEEDED) ---
const MOCK_COSTUME_ID_LOOKUP = { "Pant": 2, "Shirt": 1, "Jacket": 3, "Vest": 4 };
const MOCK_STANDARD_SIZE_ID_LOOKUP = { "Pant": 8, "Shirt": 10, "Jacket": 12, "Vest": 14 };

const getUniqueOptions = (list) => {
  const unique = [...new Set(list.filter(Boolean))];
  return unique;
};


export default function EditOrder() {
  const navigate = useNavigate();
  const location = useLocation();

  const orderDataFromState = location.state?.orderData;

  const getOrderId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('id');
  };

  // --- State Initialization ---
  const [orderId] = useState(getOrderId());
  const [deletedItemIds, setDeletedItemIds] = useState([]);

  const [costumes, setCostumes] = useState([{
    id: Date.now(),
    costume_name: '',
    assigned_employee_id: '',
    standard_size_id: '',
    quantity: 1,
    order_item_id: null
  }]);

  // Order Metadata
  const [customerName, setCustomerName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [trialDate, setTrialDate] = useState('');
  const [urgency, setUrgency] = useState('Normal');
  const [status, setStatus] = useState('Processing');
  const [totalAmount, setTotalAmount] = useState(0);
  const [advancePayment, setAdvancePayment] = useState(0);
  const [paymentMode, setPaymentMode] = useState('');

  // Measurements
  const [measurements, setMeasurements] = useState({
    shoulder: '', chest: '', waist: '', hip: '', sleeve_length: '', shirt_length: '',
    pant_length: '', thigh: '', knee: '', bottom: '', neck: '', armhole: '', wrist: ''
  });

  // UI/Loading States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Lookups
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [costumeOptions, setCostumeOptions] = useState([]);


  // --- Constants ---
  const urgencyOptions = ['Normal', 'Urgent', 'Very Urgent'];
  const statusOptions = ['On Order', 'Processing', 'Trial', 'Completed'];
  const paymentModeOptions = ['Cash', 'Online', 'Card', 'UPI', 'Cheque'];

  const measurementFields = [
    { key: 'shoulder', label: 'Shoulder', unit: 'inches' },
    { key: 'chest', label: 'Chest', unit: 'inches' },
    { key: 'waist', label: 'Waist', unit: 'inches' },
    { key: 'hip', label: 'Hip', unit: 'inches' },
    { key: 'sleeve_length', label: 'Sleeve Length', unit: 'inches' },
    { key: 'shirt_length', label: 'Shirt Length', unit: 'inches' },
    { key: 'pant_length', label: 'Pant Length', unit: 'inches' },
    { key: 'thigh', label: 'Thigh', unit: 'inches' },
    { key: 'knee', label: 'Knee', unit: 'inches' },
    { key: 'bottom', label: 'Bottom', unit: 'inches' },
    { key: 'neck', label: 'Neck', unit: 'inches' },
    { key: 'armhole', label: 'Armhole', unit: 'inches' },
    { key: 'wrist', label: 'Wrist', unit: 'inches' }
  ];

  // --- Effects & Data Fetching ---

  /**
   * FIX 2: Ensure costume list is fetched on mount for the dropdown.
   */
  const fetchCostumeList = async () => {
    try {
      const res = await fetch(GET_COSTUME_URL);
      const data = await res.json();
      if (Array.isArray(data)) {
        // Set the costume names for the dropdown
        setCostumeOptions(data.map(c => c.name).filter(name => name));
      }
    } catch (err) {
      console.error('Error fetching costumes:', err);
    }
  };

  useEffect(() => {
    // Fetch costumes once when the component mounts
    fetchCostumeList();
  }, []);

  useEffect(() => {
    if (!orderId) {
      setSubmitMessage('⚠ No order ID provided.');
      setLoading(false);
      return;
    }
    fetchEmployees();

    if (orderDataFromState) {
      loadOrderDataFromState(orderDataFromState);
    } else {
      fetchOrderDetails();
    }
  }, [orderId]);


  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await fetch(GET_EMPLOYEE_URL);
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        const activeEmployees = data.filter(emp => emp.status === 'Active');
        setEmployees(activeEmployees);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadOrderDataFromState = (order) => {
    try {
      // Basic fields
      setCustomerName(order.customer_name || "");
      setPhoneNumber(order.customer_phone || "");
      setNotes(order.notes || "");
      setDeliveryDate(order.delivery_date || "");
      setTrialDate(order.trial_date || "");
      setUrgency(order.urgency || "Normal");
      setStatus(order.status || "Processing");
      setTotalAmount(Number(order.total_amount) || 0);
      setAdvancePayment(Number(order.advance_payment) || 0);
      setPaymentMode(order.payment_mode || "");

      // Costumes
      let formattedCostumes = [];
      if (order.items && Array.isArray(order.items)) {
        formattedCostumes = order.items.map((c, i) => ({
          id: c.id || Date.now() + i,
          costume_name: c.costume_name || "",
          quantity: Number(c.quantity) || 1,
          standard_size_id: c.standard_size_id || "",
          assigned_employee_id: c.assigned_employee_id || "",
          order_item_id: c.id || null,
        }));
        setCostumes(formattedCostumes);
      } else {
        setCostumes([{
          id: Date.now(),
          costume_name: '', assigned_employee_id: '', standard_size_id: '', quantity: 1, order_item_id: null
        }]);
      }

      // Measurements
      if (order.items && order.items.length > 0 && Array.isArray(order.items[0].measurements)) {
        const item = order.items[0];
        const mapped = {};
        item.measurements.forEach((m) => {
          // Normalize measurement names for state mapping (e.g., "Sleeve Length" -> "sleeve_length")
          const name = m.measurement_name.toLowerCase().replace(/ /g, '_');
          const value = m.measurement_value || "";
          if (measurementFields.some(f => f.key === name)) {
            mapped[name] = value;
          }
        });
        setMeasurements(prev => ({ ...prev, ...mapped }));
      }

      setLoading(false);
    } catch (err) {
      console.error("Error in loadOrderDataFromState:", err);
      fetchOrderDetails();
    }
  };


  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(GET_ORDERS_URL);
      const data = await response.json();

      if (data && Array.isArray(data)) {
        const order = data.find(o => String(o.id) === orderId);
        if (order) {
          loadOrderDataFromState(order);
        } else {
          setSubmitMessage('⚠ Order not found. Please check the order ID.');
          setLoading(false);
        }
      } else {
        setSubmitMessage('✗ Failed to load order details: Invalid response format.');
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      setSubmitMessage('✗ Failed to load order details.');
      setLoading(false);
    }
  };

  // --- Local State Handlers ---

  const addCostume = () => {
    setCostumes([...costumes, {
      id: Date.now(),
      costume_name: '',
      assigned_employee_id: '',
      standard_size_id: '',
      quantity: 1,
      order_item_id: null
    }]);
  };

  const removeCostume = (id) => {
    const itemToRemove = costumes.find(c => c.id === id);

    if (itemToRemove && itemToRemove.order_item_id) {
      setDeletedItemIds(prev => [...prev, itemToRemove.order_item_id]);
    }

    setCostumes(costumes.filter(c => c.id !== id));
  };

  const updateCostume = (id, field, value) => {
    setCostumes(costumes.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const updateMeasurement = (field, value) => {
    setMeasurements(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const mapStatusToBackend = (status) => {
    if (status === "Trial") return "Ready for Trial";
    return status;
  }

  // --- Submission Logic ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const numericOrderId = parseInt(orderId);
    if (isNaN(numericOrderId)) {
      setSubmitMessage('⚠ Invalid order ID.');
      setIsSubmitting(false);
      return;
    }

    let overallSuccess = true;
    let successMessages = [];

    // 1. Prepare Measurements for API
    const measurementArray = Object.keys(measurements)
      .filter(key => measurements[key] !== "" && measurements[key] !== null)
      .map(key => ({
        // Use the clean 'Label' or fallback to a capitalized/spaced key
        measurement_name: measurementFields.find(f => f.key === key)?.label || key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        measurement_value: measurements[key]
      }));


    // 2. Prepare Item Updates/Deletions/Creations
    const existingItems = costumes.filter(c => c.order_item_id !== null);
    const newItems = costumes.filter(c => c.order_item_id === null);

    // --- STEP 1: Update Order Metadata and Measurements (FIX 1 implemented here)---
    try {
      const metadataPayload = {
        order_id: numericOrderId,
        customer_name: customerName,
        customer_phone: phoneNumber,

        status: mapStatusToBackend(status),
        delivery_date: deliveryDate,
        trial_date: trialDate,
        urgency,
        total_amount: parseFloat(totalAmount),
        advance_payment: parseFloat(advancePayment),
        payment_mode: paymentMode,
        notes,
        // Sending measurements in the 'items' array of the main edit endpoint
        items: existingItems.map(c => ({
          order_item_id: c.order_item_id,
          costume_name: c.costume_name,
          quantity: c.quantity,
          assigned_employee_id: c.assigned_employee_id,
          // **THIS SENDS THE UPDATED MEASUREMENTS**
          measurements: measurementArray
        }))
      };

      const response = await axios.post(EDIT_ORDERS_URL, metadataPayload, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data?.success) {
        successMessages.push("Order details, customer info, and measurements updated.");
      } else {
        overallSuccess = false;
        successMessages.push(`✗ Metadata Update failed: ${response.data?.error || "Unknown error"}`);
      }
    } catch (error) {
      overallSuccess = false;
      successMessages.push(`✗ Failed to update order metadata: ${error.message}`);
    }

    // --- STEP 2: Handle Deleted Items ---
    for (const itemId of deletedItemIds) {
      try {
        const deleteResponse = await axios.post(DELETE_ITEM_URL, { order_item_id: itemId });
        if (deleteResponse.data?.success) {
          successMessages.push(`Item ID ${itemId} deleted.`);
        } else {
          overallSuccess = false;
          successMessages.push(`✗ Failed to delete item ID ${itemId}: ${deleteResponse.data?.error || "Unknown error"}`);
        }
      } catch (error) {
        overallSuccess = false;
        successMessages.push(`✗ Failed to delete item ID ${itemId}.`);
      }
    }

    // --- STEP 3: Handle New Items (Add) ---
    let addedCostumes = [];
    for (const newItem of newItems) {
      const costume_id = MOCK_COSTUME_ID_LOOKUP[newItem.costume_name];
      const standard_size_id = MOCK_STANDARD_SIZE_ID_LOOKUP[newItem.costume_name];

      if (!costume_id || !standard_size_id) {
        overallSuccess = false;
        successMessages.push(`✗ Cannot add new item '${newItem.costume_name}'. Missing ID mapping or costume selection.`);
        continue;
      }

      try {
        const addPayload = {
          order_id: numericOrderId,
          costume_id: costume_id,
          assigned_employee_id: parseInt(newItem.assigned_employee_id),
          standard_size_id: standard_size_id,
        };

        const addResponse = await axios.post(ADD_ITEM_URL, addPayload);

        if (addResponse.data?.success) {
          successMessages.push(`✓ New item '${newItem.costume_name}' added.`);
          addedCostumes.push({
            ...newItem,
            order_item_id: addResponse.data.order_item_id
          });
        } else {
          overallSuccess = false;
          successMessages.push(`✗ Failed to add item '${newItem.costume_name}': ${addResponse.data?.error || "Unknown error"}`);
        }
      } catch (error) {
        overallSuccess = false;
        successMessages.push(`✗ Failed to add new item: ${error.message}`);
      }
    }

    // Final state update
    const newCostumesState = [
      ...existingItems.filter(c => !deletedItemIds.includes(c.order_item_id)), // Keep existing items not deleted
      ...addedCostumes
    ];
    setCostumes(newCostumesState.length > 0 ? newCostumesState : [{ id: Date.now(), costume_name: '', assigned_employee_id: '', standard_size_id: '', quantity: 1, order_item_id: null }]);
    setDeletedItemIds([]);

    // --- Final Message ---
    const finalMessage = successMessages.map(msg => `<li>${msg}</li>`).join('');
    setSubmitMessage(`
            ${overallSuccess ? "✓ Order update sequence completed successfully!" : "⚠ Order update completed with errors. Scroll down to see details."}
            <ul class="list-disc list-inside mt-2 text-sm">${finalMessage}</ul>
        `);

    setIsSubmitting(false);
    // Re-fetch data to sync local state with backend changes
    fetchOrderDetails();
  };

  // --- Render Logic ---

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-blue-700 font-semibold">Loading order details...</div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 bg-white rounded-lg shadow hover:bg-blue-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-blue-700" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-blue-800">
              Edit Order #{orderId}
            </h1>
            {orderDataFromState && (
              <p className="text-sm text-blue-600 mt-1">
                ✓ Loaded from order listing
              </p>
            )}
          </div>
        </div>

        {submitMessage && (
          <div
            className={`mb-4 p-3 rounded-lg ${submitMessage.includes('✓') && !submitMessage.includes('✗')
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
              }`}
            dangerouslySetInnerHTML={{ __html: submitMessage }}
          />
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-200">
            <h2 className="text-lg font-semibold text-blue-800 mb-4">Customer Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                  required
                />
              </div>
            </div>
          </div>

          {/* Customer Measurements */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-200">
            <div className="flex items-center gap-2 mb-4">
              <Ruler className="w-5 h-5 text-blue-700" />
              <h2 className="text-lg font-semibold text-blue-800">Customer Measurements</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {measurementFields.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    {field.label} ({field.unit})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={measurements[field.key] || ""}
                    onChange={(e) => updateMeasurement(field.key, e.target.value)}
                    className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                    placeholder="0.00"
                  />
                </div>
              ))}
            </div>
          </div>


          {/* Costumes */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-200 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-blue-800">Costumes</h2>

              <button
                type="button"
                onClick={addCostume}
                className="flex items-center gap-2 px-3 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Costume
              </button>
            </div>

            <div className="space-y-4">
              {costumes.map((costume, index) => (
                <div
                  key={costume.id}
                  className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

                    {/* Costume Type */}
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">
                        Costume Type
                      </label>
                      <select
                        value={costume.costume_name || ""}
                        onChange={(e) =>
                          updateCostume(costume.id, "costume_name", e.target.value)
                        }
                        className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                      >
                        <option value="">Select...</option>
                        {/* FIX 2: Check if options are loaded */}
                        {costumeOptions.length > 0 ? (
                          costumeOptions.map(opt => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>Loading Costumes...</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">
                        Assigned Employee
                      </label>
                      <select
                        value={costume.assigned_employee_id}
                        onChange={(e) => updateCostume(costume.id, 'assigned_employee_id', e.target.value)}
                        className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                        disabled={loadingEmployees}
                      >
                        <option value="">Select...</option>
                        {employees.map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={costume.quantity}
                        onChange={(e) => updateCostume(costume.id, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => removeCostume(costume.id)}
                        disabled={costumes.length === 1 && costume.order_item_id !== null}
                        className="w-full p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-200 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Urgency</label>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
              >
                {urgencyOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
              >
                {statusOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Delivery Date</label>
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Trial Date</label>
              <input
                type="date"
                value={trialDate}
                onChange={(e) => setTrialDate(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Total Amount</label>
              <input
                type="number"
                value={totalAmount}
                onChange={(e) => setTotalAmount(parseFloat(e.target.value) || 0)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Advance Payment</label>
              <input
                type="number"
                value={advancePayment}
                onChange={(e) => setAdvancePayment(parseFloat(e.target.value) || 0)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Payment Mode</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
              >
                <option value="">Select...</option>
                {paymentModeOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-blue-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                rows={3}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-right">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}